class AmprioHeader {
  constructor() {
    this.isCartFetching = false;
    this.pendingCartUpdate = false;
    this.cachedElements = new Map();
    this.cartUpdateScheduled = false;
    this.lastItemCount = null;
    this.cartCache = null;
    this.cartCacheTime = 0;
    this.cartCacheTTL = 5000; // 5 секунд кеш

    this.init();
  }

  getElement(selector) {
    if (!this.cachedElements.has(selector)) {
      this.cachedElements.set(selector, document.querySelector(selector));
    }
    return this.cachedElements.get(selector);
  }

  getElements(selector) {
    // Не кешируем NodeList, всегда получаем свежий список элементов
    // чтобы не пропустить динамически добавленные элементы
    return Array.from(document.querySelectorAll(selector));
  }

  init() {
    this.initCartUpdateEvents();
    this.initStickyHeader();
    this.initTabletMenuScroll();

    // Отложить загрузку корзины до полной загрузки страницы
    if (document.readyState === 'complete') {
      this.updateCartCount();
    } else {
      window.addEventListener('load', () => {
        // Дать время другим скриптам загрузиться
        setTimeout(() => this.updateCartCount(), 100);
      });
    }
  }

  initTabletMenuScroll() {
    const centerActiveMenuItem = () => {
      if (window.innerWidth <= 1199 && window.innerWidth >= 769) {
        const menuContainer = document.querySelector('.block_navigation_menu__container');
        const activeMenuItem = document.querySelector('.btn_menu_act');
        
        if (menuContainer && activeMenuItem) {
          // Получаем позиции относительно viewport
          const containerRect = menuContainer.getBoundingClientRect();
          const itemRect = activeMenuItem.getBoundingClientRect();
          
          // Вычисляем позицию элемента относительно контейнера
          const itemRelativeLeft = itemRect.left - containerRect.left + menuContainer.scrollLeft;
          
          // Вычисляем центр элемента
          const itemCenter = itemRelativeLeft + (itemRect.width / 2);
          
          // Вычисляем центр видимой области контейнера
          const containerCenter = menuContainer.scrollLeft + (containerRect.width / 2);
          
          // Вычисляем необходимый scroll для центрирования
          const scrollLeft = itemCenter - (containerRect.width / 2);
          
          menuContainer.scrollTo({
            left: Math.max(0, scrollLeft),
            behavior: 'smooth'
          });
        }
      }
    };

    // Выполняем после полной загрузки DOM и стилей
    const runCenter = () => {
      // Даем время на применение стилей
      setTimeout(() => {
        centerActiveMenuItem();
      }, 150);
    };

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', runCenter);
      // Также после полной загрузки страницы
      window.addEventListener('load', runCenter);
    } else {
      runCenter();
    }

    // Re-center on resize
    let resizeTimer;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(centerActiveMenuItem, 250);
    });

    // Также центрируем при изменении активного пункта меню
    const observer = new MutationObserver((mutations) => {
      // Проверяем, изменился ли класс активного элемента
      const hasActiveChange = mutations.some(mutation => 
        mutation.type === 'attributes' && 
        mutation.attributeName === 'class' &&
        (mutation.target.classList.contains('btn_menu_act') || 
         Array.from(mutation.target.querySelectorAll('.btn_menu_act')).length > 0)
      );
      
      if (hasActiveChange) {
        setTimeout(centerActiveMenuItem, 100);
      }
    });

    const menuContainer = document.querySelector('.block_navigation_menu__container');
    if (menuContainer) {
      observer.observe(menuContainer, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['class']
      });
    }
  }

  initCartUpdateEvents() {
    let debounceTimer;
    
    const scheduleUpdate = (e) => {
      // Поддержка разных форматов событий
      // 1. Формат quick-add-button (event.detail.cart.item_count)
      // 2. Формат ThemeEvents (event.detail.data.itemCount)
      // 3. Прямое значение (event.detail.item_count)
      const itemCount = e?.detail?.cart?.item_count 
        ?? e?.detail?.data?.itemCount 
        ?? e?.detail?.item_count;
      
      // Если в событии есть данные корзины - обновляем сразу
      if (e && e.detail && itemCount !== undefined) {
        // Всегда обновляем, проверка на изменения внутри applyCartCount
        this.applyCartCount(itemCount);
        
        // Если есть полная корзина, обновляем кеш
        if (e.detail.cart) {
          // Инвалидируем глобальный кеш
          if (window.cartCache) {
            window.cartCache.invalidate();
          }
          // Обновляем кеш с новыми данными
          if (window.setCart) {
            window.setCart(e.detail.cart);
          }
        }
        return;
      }
      
      // Если данных нет - инвалидируем кеш и делаем запрос
      if (window.cartCache) {
        window.cartCache.invalidate();
      }
      
      // Debounce для частых обновлений, но только если нет данных
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => {
        this.updateCartCount();
      }, 100); // Уменьшено с 150 до 100мс
    };

    // Используем только события, без перехвата fetch
    [
      'shopify:cart:update',
      'cart:update',
      'cart:add',
      'cart:change',
      'cart:updated'
    ].forEach(event =>
      document.addEventListener(event, scheduleUpdate, { passive: true })
    );
  }

  updateCartCount(count = null) {
    const textElements = this.getElements('.cart-count__text');
    const containerElements = this.getElements('.cart-count');
    if (!textElements.length || !containerElements.length) return;

    // Если передан явный count - используем его
    if (count !== null) {
      this.applyCartCount(count);
      return;
    }

    // Используем глобальный кеш
    if (window.cartCache) {
      window.cartCache.get()
        .then(cart => {
          if (cart) {
            this.applyCartCount(cart.item_count);
          }
        })
        .catch(() => {
          // Игнорируем ошибки
        });
    } else {
      // Fallback если кеш еще не загружен
      fetch('/cart.js')
        .then(res => res.json())
        .then(cart => {
          this.applyCartCount(cart.item_count);
        })
        .catch(() => {});
    }
  }

  applyCartCount(count) {
    const newCount = parseInt(count) || 0;
    
    // Всегда обновляем, чтобы гарантировать синхронизацию DOM
    // (даже если значение не изменилось, DOM может быть не синхронизирован)
    this.lastItemCount = newCount;

    // Обновляем все счетчики синхронно, без задержки
    // Всегда получаем свежий список элементов
    const textElements = Array.from(document.querySelectorAll('.cart-count__text'));
    const containerElements = Array.from(document.querySelectorAll('.cart-count'));

    textElements.forEach(textEl => {
      if (textEl) {
        textEl.textContent = newCount;
      }
    });

    containerElements.forEach(containerEl => {
      if (!containerEl) return;

      if (newCount > 0) {
        containerEl.style.display = 'flex';
        containerEl.classList.add('cart-has-items');
      } else {
        containerEl.classList.remove('cart-has-items');
      }

      containerEl.style.opacity = '1';
    });
  }

  initStickyHeader() {
    const header = this.getElement('.amprio-header');
    const menu = this.getElement('.block_navigation_menu');
    if (!header || !menu) return;

    const mode = header.dataset.sticky;
    if (mode === 'never') return;

    let lastY = window.scrollY;
    let isSticky = false;
    let ticking = false;

    const update = () => {
      const y = window.scrollY;
      const threshold = header.offsetHeight;

      if (mode === 'always') {
        const shouldBeSticky = y > threshold;
        if (shouldBeSticky !== isSticky) {
          menu.classList.toggle('block_navigation_menu--sticky', shouldBeSticky);
          isSticky = shouldBeSticky;
        }

      } else if (mode === 'scroll-up') {
        if (y > threshold) {
          const scrollingDown = y > lastY && y > 120;
          const shouldBeSticky = !scrollingDown;
          if (shouldBeSticky !== isSticky) {
            menu.classList.toggle('block_navigation_menu--sticky', shouldBeSticky);
            isSticky = shouldBeSticky;
          }
        } else if (isSticky) {
          menu.classList.remove('block_navigation_menu--sticky');
          isSticky = false;
        }
      }

      lastY = y;
      ticking = false;
    };

    window.addEventListener('scroll', () => {
      if (!ticking) {
        requestAnimationFrame(update);
        ticking = true;
      }
    });
  }
}

window.openCart = function () {
  const drawer = document.querySelector('cart-drawer-component');
  if (drawer?.open) drawer.open();
  else location.href = '/cart';
};

window.updateCartCount = function (count = null) {
  window.amprioHeaderInstance?.updateCartCount(count);
};

function initHeaderOnce() {
  if (!window.amprioHeaderInstance) {
    window.amprioHeaderInstance = new AmprioHeader();
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initHeaderOnce);
} else {
  initHeaderOnce();
}

document.addEventListener('shopify:section:load', initHeaderOnce);

if (typeof module !== 'undefined') module.exports = AmprioHeader;